// frontend/src/pages/BookingPage.jsx

import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  Users, Mail, Phone,
  Check, Shield, Clock, Lock,
  CreditCard, CheckCircle2,
  Landmark, Copy, Info, ArrowRight, ChevronLeft,
  MapPin, Star, Zap, AlertCircle,
} from 'lucide-react';
import {
  PricingBreakdown,
  useBookingPricingLines,
} from '../components/PricingBreakdown';
import { TouristComposer } from '../components/TouristComposer';
import { AUTH_TOKEN_KEY } from '../lib/apolloClient';

const parsePrice = (v) =>
  typeof v === 'string' ? parseFloat(v) || 0 : typeof v === 'number' ? v : 0;

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.12)';
const NAVY     = '#080e1a';

/* ─── GraphQL ─── */
const GET_TOUR = gql`
  query GetTourForBooking($slug: String!) {
    tour(slug: $slug) {
      id name slug tagline featuredImage
      currentPrice durationLabel maxParticipants
      totalReviews
      category { name }
      parks { name country { name code } }
      cancellationPolicy { name freeDateChanges freeDateChangesNote }
      upcomingDepartures(limit: 6) {
        id departureDate returnDate
        spotsRemaining availableSpots capacityPct status
      }
      agency { id name phone primaryEmail }
    }
  }
`;

const GET_PRICING_PREVIEW = gql`
  query GetPricingPreview($tourId: ID!, $tourists: [TouristCompositionInput!], $startDate: Date) {
    pricingPreview(tourId: $tourId, tourists: $tourists, startDate: $startDate) {
      totalPrice pricePerPerson subtotal tax
      taxRatePct
      taxLabel
      breakdown
    }
  }
`;

const BOOK_TOUR = gql`
  mutation BookTour($input: BookTourInput!) {
    bookTour(input: $input) {
      booking {
        id bookingReference status paymentStatus
        totalPrice amountDue guestName guestEmail
        startDate endDate numberOfTourists portalUrl
      }
      message portalUrl token
    }
  }
`;

const AGENCY_PAYMENT_METHODS = gql`
  query AgencyPaymentMethods($bookingId: ID!) {
    agencyPaymentMethods(bookingId: $bookingId) {
      methods {
        accountType displayName currency isGateway isManual instructions
        bankName accountName accountNumber iban swiftBic routingNumber
        bankAddress intermediaryBankSwift intermediaryBankName
      }
    }
  }
`;

const INITIATE_PAYMENT = gql`
  mutation InitiatePayment($input: InitiatePaymentInput!) {
    initiatePayment(input: $input) {
      transaction { id transactionId amount currency status }
      checkoutUrl paymentType message
    }
  }
`;

const CONFIRM_BANK_TRANSFER = gql`
  mutation ConfirmBankTransfer($input: ConfirmBankTransferInput!) {
    confirmBankTransfer(input: $input) {
      transaction { id transactionId status }
      success message
    }
  }
`;

/* ─── Styles ─── */
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@300;400;500;600&display=swap');

    .f-c { font-family: 'Cormorant Garamond', serif; }
    .f-j { font-family: 'Jost', sans-serif; }

    .field-label {
      display: block; font-family: 'Jost', sans-serif; font-size:13px;
      font-weight: 600; letter-spacing:0.2em; text-transform: uppercase;
      color:#12100e; margin-bottom: 7px;
    }
    .field-input {
      width: 100%; font-family: 'Jost', sans-serif; font-size:16px;
      color: #0f172a; background: #fff; border: 1px solid #ddd9d2;
      padding: 12px 14px; outline: none; transition: border-color 0.2s;
      appearance: none;
    }
    .field-input:focus { border-color: ${GOLD}; }
    .field-input::placeholder { color: #b0aaa0; }

    .dep-chip {
      padding: 12px 14px; border: 1px solid #ddd9d2;
      cursor: pointer; background: #fff; transition: all 0.2s; text-align: left;
      width: 100%;
    }
    .dep-chip:hover:not(:disabled) { border-color: ${GOLD}; }
    .dep-chip.active { border-color: ${GOLD}; background: ${GOLD_DIM}; box-shadow: inset 2px 0 0 ${GOLD}; }
    .dep-chip:disabled { opacity: 0.4; cursor: not-allowed; }

    .primary-cta {
      width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px;
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase;
      padding: 17px 28px; background: ${NAVY}; color: #fff;
      border: 1px solid ${NAVY}; cursor: pointer; transition: all 0.3s;
      position: relative; overflow: hidden;
    }
    .primary-cta::before {
      content: ''; position: absolute; inset: 0; background: ${GOLD};
      transform: scaleX(0); transform-origin: left;
      transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0;
    }
    .primary-cta:hover:not(:disabled)::before { transform: scaleX(1); }
    .primary-cta:hover:not(:disabled) { color: ${NAVY}; }
    .primary-cta:disabled { opacity: 0.45; cursor: not-allowed; }
    .primary-cta > * { position: relative; z-index: 1; }

    .ghost-cta {
      width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase; padding: 13px 20px;
      border: 1px solid #ddd9d2; color:#12100e; background: #fff;
      cursor: pointer; transition: all 0.2s;
    }
    .ghost-cta:hover { border-color: ${NAVY}; color: ${NAVY}; }

    .cap-bar { height: 2px; background: #ede9e2; overflow: hidden; border-radius: 1px; }
    .cap-bar-fill { height: 100%; background: ${GOLD}; transition: width 0.5s; }

    .gold-rule { height: 1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,0.08) 100%); }

    .bank-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 11px 0; border-bottom: 1px solid #f0ece6; font-family: 'Jost', sans-serif;
    }
    .bank-row:last-child { border-bottom: none; }

    .proof-input {
      width: 100%; font-family: 'Jost', sans-serif; font-size:16px;
      color: #0f172a; background: #fff; border: 1px solid #ddd9d2;
      padding: 11px 13px; outline: none; transition: border-color 0.2s; box-sizing: border-box;
    }
    .proof-input:focus { border-color: ${GOLD}; }
    .proof-input::placeholder { color: #b0aaa0; }

    .summary-card { border: 1px solid #e2ddd6; background: #fff; }
    .trust-item { display: flex; align-items: center; gap: 10px; padding: 11px 0; border-bottom: 1px solid #f0ece6; }
    .trust-item:last-child { border-bottom: none; }
    .copy-btn { cursor: pointer; color:#12100e; transition: color 0.2s; background: none; border: none; }
    .copy-btn:hover { color: ${GOLD}; }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .fade-up { animation: fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }

    @keyframes popIn {
      0%   { transform: scale(0.6); opacity: 0; }
      70%  { transform: scale(1.08); }
      100% { transform: scale(1); opacity: 1; }
    }
    .pop-in { animation: popIn 0.55s cubic-bezier(0.22,1,0.36,1) both; }
  `}</style>
);

/* ─── Tiny helpers ─── */
const SL = ({ text }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-5 h-px" style={{ background: GOLD }} />
    <span className="f-j text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>{text}</span>
  </div>
);

// A date-only 'YYYY-MM-DD' is read as local midnight — new Date('2026-10-26')
// is UTC midnight and shows as 25 Oct anywhere west of Greenwich.
const formatDate = (d) =>
  d ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d)
        .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const todayISO = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

const fmt = (n, cur = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: cur,
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);

/* ════════════════════════════════════════
   BOOKING PAGE
════════════════════════════════════════ */
export default function BookingPage() {
  const { tourId: slug } = useParams();

  /* ── State ── */
  const [step, setStep]                   = useState(1);
  const [payMethod, setPayMethod]         = useState('bank');
  const [bankStage, setBankStage]         = useState('details');
  const [selectedDep, setSelectedDep]     = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError]                 = useState('');
  const [copied, setCopied]               = useState(null);

  const [proof, setProof] = useState({ amount: '', reference: '', txnId: '', date: '', notes: '' });
  const [pendingTxnId, setPendingTxnId] = useState(null);

  /* ── Lead guest ── */
  const [lead, setLead] = useState({ firstName: '', lastName: '', email: '', phone: '', specialRequests: '' });

  /* ── Tourists ── */
  const [tourists, setTourists] = useState([{ touristType: 'adult', nationality: '' }]);

  /* ── Queries ── */
  const { data, loading } = useQuery(GET_TOUR, { variables: { slug }, fetchPolicy: 'cache-first' });

  const tour = data?.tour;

  const touristsForPreview = tourists.map(t => ({
    touristType: t.touristType,
    nationality: t.nationality || 'INT',
  }));

  const { data: previewData } = useQuery(GET_PRICING_PREVIEW, {
    variables: {
      tourId:    tour?.id,
      tourists:  touristsForPreview,
      startDate: selectedDep?.departureDate || null,
    },
    skip: !tour?.id || tourists.some(t => !t.nationality),
    fetchPolicy: 'network-only',
  });

  const pricing    = previewData?.pricingPreview;
  const finalTotal = parsePrice(pricing?.totalPrice);
  const perPerson  = parsePrice(pricing?.pricePerPerson);
  const subtotal   = parsePrice(pricing?.subtotal);
  const tax        = parsePrice(pricing?.tax);

  /* ── Pricing lines for sidebar ── */
  // Note: countries comes from TouristComposer's own query; pass [] here —
  // useBookingPricingLines only needs it for who-label derivation which the
  // hook also handles from fee.nationalities when the backend provides them.
  const lines = useBookingPricingLines({ pricing, tourists, countries: [], tour });

  /* ── Bank methods (lazy) ── */
  const [fetchBankMethods, { data: methodsData, loading: methodsLoading, error: methodsError }] =
    useLazyQuery(AGENCY_PAYMENT_METHODS, { fetchPolicy: 'cache-first' });
  const bankMethod = methodsData?.agencyPaymentMethods?.methods?.find(m => m.accountType === 'bank_transfer') ?? null;

  /* ── Mutations ── */
  const [bookTour,        { loading: booking }]    = useMutation(BOOK_TOUR);
  const [initiatePayment, { loading: initiating }] = useMutation(INITIATE_PAYMENT);
  const [confirmTransfer, { loading: confirming }] = useMutation(CONFIRM_BANK_TRANSFER);
  const submitting = booking || initiating || confirming;

  /* ── Max tourists cap (enforced by TouristComposer via prop) ── */
  const maxTourists = selectedDep
    ? Math.min(tour?.maxParticipants || 12, selectedDep.spotsRemaining)
    : (tour?.maxParticipants || 12);

  /* ── Step 1 validation ── */
  const step1Valid =
    lead.firstName &&
    lead.email &&
    selectedDep &&
    tourists.every(t => t.nationality);

  /* ── Submit booking ── */
  const handleBook = async () => {
    setError('');
    try {
      const { data } = await bookTour({
        variables: {
          input: {
            tourId:           tour.id,
            guestName:        `${lead.firstName} ${lead.lastName}`.trim(),
            guestEmail:       lead.email,
            guestPhone:       lead.phone,
            numberOfTourists: tourists.length,
            tourists: tourists.map((t, i) => ({
              firstName:        i === 0 ? lead.firstName : '',
              lastName:         i === 0 ? lead.lastName  : '',
              nationality:      t.nationality || 'INT',
              touristType:      t.touristType,
              isPrimaryContact: i === 0,
              passportNumber:   '',
            })),
            nationality:         tourists[0]?.nationality || 'INT',
            specialRequirements: lead.specialRequests,
            tourDepartureId:     selectedDep.id,
          },
        },
      });

      const result = data?.bookTour?.booking;
      if (!result) throw new Error('Booking failed — please try again.');
      setBookingResult(result);
      // agencyPaymentMethods requires an authenticated request. BookTour
      // creates/logs in the guest and hands back a token for exactly this —
      // without storing it first, the very next query goes out with no
      // Authorization header, the backend rejects it, and the bank-details
      // panel is stuck on its "loading" state forever with no visible error.
      if (data?.bookTour?.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.bookTour.token);
      }
      if (result.id) fetchBankMethods({ variables: { bookingId: result.id } });
      setStep(2);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    }
  };

  /* ── Bank transfer proof ── */
  const handleSubmitProof = async () => {
    setError('');
    const parsedAmount = parseFloat(proof.amount) || 0;
    if (!parsedAmount)           { setError('Enter the exact amount you transferred.'); return; }
    if (!proof.reference.trim()) { setError('Bank transfer reference is required.'); return; }
    if (!proof.txnId.trim())     { setError('Bank transaction ID is required.'); return; }

    try {
      let txnId = pendingTxnId;
      if (!txnId) {
        const { data: initData } = await initiatePayment({
          variables: {
            input: {
              bookingId:         bookingResult.id,
              amount:            parsedAmount.toFixed(2),
              currency:          'USD',
              paymentMethodSlug: 'bank_transfer',
            },
          },
        });
        txnId = initData?.initiatePayment?.transaction?.id;
        if (!txnId) throw new Error('Failed to register transfer.');
        setPendingTxnId(txnId);
      }

      const { data: confirmData } = await confirmTransfer({
        variables: {
          input: {
            transactionId:      txnId,
            proofAmount:        parsedAmount.toFixed(2),
            proofReference:     proof.reference.trim(),
            proofTransactionId: proof.txnId.trim(),
            ...(proof.date  ? { proofDate:  proof.date }         : {}),
            ...(proof.notes ? { proofNotes: proof.notes.trim() } : {}),
          },
        },
      });
      if (!confirmData?.confirmBankTransfer?.success) throw new Error('Confirmation failed.');
      setStep(3);
    } catch (e) {
      setError(e.message || 'Submission failed. Please try again.');
    }
  };

  /* ── Card payment ── */
  const handleCardPay = async () => {
    setError('');
    try {
      const { data } = await initiatePayment({
        variables: {
          input: {
            bookingId:         bookingResult.id,
            amount:            finalTotal.toFixed(2),
            currency:          'USD',
            paymentMethodSlug: 'card',
            gateway:           'flutterwave',
          },
        },
      });
      const url = data?.initiatePayment?.checkoutUrl;
      if (url) { window.location.href = url; return; }
      throw new Error('No checkout URL.');
    } catch (e) {
      setError(e.message || 'Payment initiation failed.');
    }
  };

  const copy = (val, key) => {
    navigator.clipboard?.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  /* ── Derived ── */
  const location = useMemo(() => {
    if (!tour?.parks?.length) return '';
    return [...new Set(tour.parks.map(p => p.country?.name).filter(Boolean))].join(', ');
  }, [tour]);

  /* ── Loading ── */
  if (loading || !tour) return (
    <div className="f-j min-h-screen flex items-center justify-center" style={{ background: '#F5F3EF' }}>
      <PageStyles />
      <p className="f-j text-[15px] uppercase tracking-[0.16em] text-slate-400">Loading…</p>
    </div>
  );

  const hasBookableDates = tour.upcomingDepartures.some(d => d.spotsRemaining > 0);

  /* ════════════════════════════════
     RENDER
  ════════════════════════════════ */
  return (
    <div className="f-j min-h-screen pb-24" style={{ background: '#F5F3EF' }}>
      <PageStyles />

      {/* ── Header ── */}
      <div className="relative overflow-hidden dark-sec" style={{ background: NAVY, paddingTop: '5.5rem', paddingBottom: '3.5rem' }}>
        <img src={tour.featuredImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(8,14,26,0.95) 40%, rgba(8,14,26,0.6))' }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          {step < 3 && (
            <Link to={`/tours/${slug}`} className="inline-flex items-center gap-2 mb-5 f-j text-[13px] uppercase tracking-[0.16em] text-white/40 hover:text-white/70 transition-colors">
              <ChevronLeft size={12} /> Back
            </Link>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-6 h-px" style={{ background: GOLD }} />
            <span className="f-j text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
              {step === 1 ? 'Reservation' : step === 2 ? 'Secure Checkout' : 'Confirmed'}
            </span>
          </div>
          <h1 className="f-c text-white font-light leading-tight" style={{ fontSize: 'clamp(36px,5vw,56px)' }}>
            {step === 1 && 'Your Safari Begins Here'}
            {step === 2 && 'Secure Your Safari'}
            {step === 3 && "You're All Set"}
          </h1>
          <p className="f-j text-[15px] mt-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{tour.name}</p>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* ══ MAIN COLUMN ══ */}
          <div className="lg:col-span-8 space-y-5">

            {/* Step indicator */}
            {step < 3 && hasBookableDates && (
              <div className="flex items-center gap-3 bg-white border px-6 py-4" style={{ borderColor: '#e2ddd6' }}>
                {[{ n: 1, label: 'Details' }, { n: 2, label: 'Payment' }].map((s, i) => (
                  <React.Fragment key={s.n}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 flex items-center justify-center border f-j text-[13px] font-semibold transition-all"
                        style={{
                          borderColor: step > s.n ? GOLD : step === s.n ? NAVY : '#ddd9d2',
                          background:  step > s.n ? GOLD : step === s.n ? NAVY : 'transparent',
                          color:       step > s.n ? NAVY : step === s.n ? '#fff' : '#94a3b8',
                        }}>
                        {step > s.n ? <Check size={11} strokeWidth={2.5} /> : s.n}
                      </div>
                      <span className="f-j text-[13px] uppercase tracking-[0.14em] hidden sm:block"
                        style={{ color: step >= s.n ? '#334155' : '#94a3b8' }}>{s.label}</span>
                    </div>
                    {i === 0 && <div className="flex-1 h-px max-w-[40px]" style={{ background: step > 1 ? GOLD : '#e2ddd6' }} />}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* ══════════════════════════════
                STEP 1 — DETAILS
            ══════════════════════════════ */}
            {step === 1 && (
              <div className="bg-white border fade-up" style={{ borderColor: '#e2ddd6' }}>
                <div className="p-7 sm:p-9 space-y-9">

                  {/* 1A — Departure */}
                  <section>
                    <SL text="When" />
                    <h2 className="f-c text-slate-900 font-light mb-5" style={{ fontSize: '29px' }}>
                      Choose your departure
                    </h2>
                    {(() => {
                      const allFull = tour.upcomingDepartures.length > 0 &&
                        tour.upcomingDepartures.every(d => d.spotsRemaining === 0);
                      const noDeps = tour.upcomingDepartures.length === 0;

                      if (allFull || noDeps) return (
                        <div className="fade-up">
                          {allFull && (
                            <div className="grid sm:grid-cols-2 gap-2.5 mb-5 opacity-50 pointer-events-none select-none">
                              {tour.upcomingDepartures.map(dep => (
                                <div key={dep.id} className="dep-chip" style={{ cursor: 'default' }}>
                                  <div className="f-c font-light text-slate-800 mb-1" style={{ fontSize: '21px' }}>
                                    {formatDate(dep.departureDate)}
                                  </div>
                                  <div className="f-j text-[13px] text-slate-400 mb-2">Returns {formatDate(dep.returnDate)}</div>
                                  <div className="cap-bar mb-1.5">
                                    <div className="cap-bar-fill" style={{ width: '100%' }} />
                                  </div>
                                  <div className="f-j text-[13px]" style={{ color: '#ef4444' }}>Fully booked</div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="p-6 border" style={{ borderColor: 'rgba(184,151,90,0.3)', background: 'rgba(184,151,90,0.05)' }}>
                            <div className="flex items-start gap-4">
                              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 mt-0.5"
                                style={{ border: `1px solid rgba(184,151,90,0.35)`, background: 'rgba(184,151,90,0.08)' }}>
                                <AlertCircle size={15} strokeWidth={1.5} style={{ color: GOLD }} />
                              </div>
                              <div>
                                <p className="f-c text-slate-900 font-light mb-1" style={{ fontSize: '25px' }}>
                                  {allFull ? 'These dates are fully booked.' : 'No scheduled departures right now.'}
                                </p>
                                <p className="f-j text-[15px] text-slate-500 font-light leading-relaxed mb-4">
                                  {allFull
                                    ? 'All spots on the upcoming departures for this safari have been taken. You can design a private departure on your own dates using the Tour Builder — just tap the builder button on the page.'
                                    : "We don't have fixed departures scheduled for this safari yet. Use the Tour Builder to request your own private departure on dates that suit you — tap the builder button on the page."}
                                </p>
                                <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: 'rgba(184,151,90,0.2)' }}>
                                  <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                                  <p className="f-j text-[13px] text-slate-400 font-light">
                                    The Tour Builder lets you pick exact dates, group size, accommodation and pace — fully bespoke.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );

                      return (
                        <div>
                          <div className="grid sm:grid-cols-2 gap-2.5">
                            {tour.upcomingDepartures.map(dep => {
                              const full     = dep.spotsRemaining === 0;
                              const urgent   = dep.spotsRemaining <= 2 && !full;
                              const selected = selectedDep?.id === dep.id;
                              return (
                                <button key={dep.id} disabled={full}
                                  onClick={() => {
                                    setSelectedDep(dep);
                                    if (tourists.length > dep.spotsRemaining)
                                      setTourists(prev => prev.slice(0, dep.spotsRemaining));
                                  }}
                                  className={`dep-chip ${selected ? 'active' : ''}`}>
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="f-c font-light text-slate-800" style={{ fontSize: '21px' }}>
                                      {formatDate(dep.departureDate)}
                                    </span>
                                    {selected && <Check size={12} strokeWidth={2.5} style={{ color: GOLD }} />}
                                  </div>
                                  <div className="f-j text-[13px] text-slate-400 mb-2">Returns {formatDate(dep.returnDate)}</div>
                                  <div className="cap-bar mb-1.5">
                                    <div className="cap-bar-fill" style={{ width: `${dep.capacityPct}%` }} />
                                  </div>
                                  <div className="f-j text-[13px]" style={{ color: full ? '#ef4444' : urgent ? GOLD : '#94a3b8' }}>
                                    {full ? 'Fully booked' : urgent ? `Only ${dep.spotsRemaining} spots left` : `${dep.spotsRemaining} spots available`}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {tour.upcomingDepartures.some(d => d.spotsRemaining === 0) && (
                            <p className="f-j text-[13px] text-slate-400 mt-3 leading-relaxed">
                              Some dates are fully booked. Need different dates entirely? Use the Tour Builder on this page to request a private departure.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </section>

                  {hasBookableDates && (<>

                  <div className="gold-rule" />

                  {/* 1B — Group size */}
                  <section>
                    <SL text="Who" />
                    <h2 className="f-c text-slate-900 font-light mb-5" style={{ fontSize: '29px' }}>
                      How many guests?
                    </h2>
                    <TouristComposer
                      tourists={tourists}
                      onChange={setTourists}
                      maxTourists={maxTourists}
                    />
                  </section>

                  <div className="gold-rule" />

                  {/* 1C — Contact */}
                  <section>
                    <SL text="Contact" />
                    <h2 className="f-c text-slate-900 font-light mb-2" style={{ fontSize: '29px' }}>
                      Your details
                    </h2>
                    <p className="f-j text-[15px] text-slate-400 font-light mb-5">
                      We'll send your confirmation here and set up your safari portal. Passport details can be completed later.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="field-label">First Name <span style={{ color: GOLD }}>*</span></label>
                        <input className="field-input" placeholder="Eleanor" value={lead.firstName}
                          onChange={e => setLead(p => ({ ...p, firstName: e.target.value }))} />
                      </div>
                      <div>
                        <label className="field-label">Last Name</label>
                        <input className="field-input" placeholder="Ashworth" value={lead.lastName}
                          onChange={e => setLead(p => ({ ...p, lastName: e.target.value }))} />
                      </div>
                      <div>
                        <label className="field-label">Email <span style={{ color: GOLD }}>*</span></label>
                        <div className="relative">
                          <Mail size={13} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#12100e' }} />
                          <input className="field-input" type="email" placeholder="eleanor@email.com"
                            style={{ paddingLeft: '36px' }} value={lead.email}
                            onChange={e => setLead(p => ({ ...p, email: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <label className="field-label">Phone <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
                        <div className="relative">
                          <Phone size={13} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#12100e' }} />
                          <input className="field-input" type="tel" placeholder="+255 700 000 000"
                            style={{ paddingLeft: '36px' }} value={lead.phone}
                            onChange={e => setLead(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="field-label">Special Requests <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
                        <textarea
                          className="field-input"
                          placeholder="Dietary requirements, accessibility needs, celebrations…"
                          rows={3}
                          style={{ resize: 'vertical' }}
                          value={lead.specialRequests}
                          onChange={e => setLead(p => ({ ...p, specialRequests: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 flex items-start gap-2.5 border" style={{ borderColor: 'rgba(184,151,90,0.25)', background: GOLD_DIM }}>
                      <Info size={12} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                      <p className="f-j text-[13px] text-slate-500 leading-relaxed">
                        Passport details for each traveller can be added later in your MySafari portal — no need to have them now.
                      </p>
                    </div>
                  </section>

                  </>)}
                </div>

                {error && hasBookableDates && (
                  <div className="mx-7 mb-4 p-3 border-l-2 border-red-400 bg-red-50 f-j text-[15px] text-red-600">{error}</div>
                )}

                {hasBookableDates && (
                  <div className="px-7 sm:px-9 pb-7 sm:pb-9">
                    {!step1Valid && (
                      <p className="f-j text-[13px] text-slate-400 text-center mb-3">
                        {!selectedDep ? 'Select a departure to continue.' : 'Enter your name and email.'}
                      </p>
                    )}
                    <button onClick={handleBook} className="primary-cta" disabled={!step1Valid || submitting}>
                      <span>{submitting ? 'Processing…' : 'Continue to Payment'}</span>
                      {!submitting && <ArrowRight size={13} />}
                    </button>
                    <p className="f-j text-[13px] text-slate-400 text-center mt-3">No payment taken at this step</p>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════
                STEP 2 — PAYMENT
            ══════════════════════════════ */}
            {step === 2 && bookingResult && (
              <div className="bg-white border fade-up" style={{ borderColor: '#e2ddd6' }}>
                <div className="p-7 sm:p-9">
                  <SL text="Payment" />
                  <h2 className="f-c text-slate-900 font-light mb-6" style={{ fontSize: '29px' }}>
                    How would you like to pay?
                  </h2>

                  <div className="grid grid-cols-2 gap-3 mb-7">
                    {[
                      { key: 'bank', icon: Landmark,   label: 'Bank Transfer', sub: 'SWIFT · verified within 24h' },
                      { key: 'card', icon: CreditCard, label: 'Credit Card',   sub: 'Visa, Mastercard, Amex'      },
                    ].map(m => (
                      <button key={m.key} onClick={() => setPayMethod(m.key)}
                        className="p-4 border text-left transition-all"
                        style={{
                          borderColor: payMethod === m.key ? GOLD     : '#ddd9d2',
                          background:  payMethod === m.key ? GOLD_DIM : '#fff',
                          boxShadow:   payMethod === m.key ? `inset 2px 0 0 ${GOLD}` : 'none',
                        }}>
                        <m.icon size={15} strokeWidth={1.5} style={{ color: payMethod === m.key ? GOLD : '#94a3b8', marginBottom: 8 }} />
                        <div className="f-j text-[13px] font-semibold text-slate-700 mb-1">{m.label}</div>
                        <div className="f-j text-[13px] text-slate-400">{m.sub}</div>
                      </button>
                    ))}
                  </div>

                  {/* Not forced — the booking is already reserved and invoiced;
                      payment doesn't have to happen in this exact flow. */}
                  <div className="p-3.5 border flex items-center gap-2.5 mb-7" style={{ borderColor: '#e2ddd6', background: '#fafaf8' }}>
                    <Clock size={13} strokeWidth={1.5} className="flex-shrink-0" style={{ color: GOLD }} />
                    <p className="f-j text-[13px] text-slate-500 leading-relaxed flex-1">
                      Not ready to pay right now? Your spot is already reserved.
                    </p>
                    <button
                      className="f-j text-[13px] font-semibold uppercase tracking-[0.14em] flex-shrink-0"
                      style={{ color: GOLD, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => { setPayMethod('later'); setStep(3); }}
                    >
                      Pay later via portal
                    </button>
                  </div>

                  {/* ─ Bank transfer ─ */}
                  {payMethod === 'bank' && (
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        {[{ n: '1', lbl: 'Transfer Funds' }, { n: '2', lbl: 'Confirm Details' }].map((s, i) => (
                          <React.Fragment key={s.n}>
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 flex items-center justify-center border f-j text-[13px] font-semibold"
                                style={{
                                  borderColor: (i === 0 && bankStage === 'proof') ? GOLD : (i === 1 && bankStage === 'proof') ? NAVY : (i === 0 ? NAVY : '#ddd9d2'),
                                  background:  (i === 0 && bankStage === 'proof') ? GOLD_DIM : (i === 1 && bankStage === 'proof') ? NAVY : (i === 0 ? NAVY : 'transparent'),
                                  color:       (i === 0 && bankStage === 'proof') ? NAVY : (i === 1 && bankStage === 'proof') ? '#fff' : (i === 0 ? '#fff' : '#94a3b8'),
                                }}>
                                {i === 0 && bankStage === 'proof' ? <Check size={10} strokeWidth={2.5} /> : s.n}
                              </div>
                              <span className="f-j text-[13px] uppercase tracking-[0.14em]"
                                style={{ color: (i === 1 && bankStage === 'proof') ? '#334155' : (i === 0 && bankStage !== 'proof') ? '#334155' : '#94a3b8' }}>
                                {s.lbl}
                              </span>
                            </div>
                            {i === 0 && <div className="flex-1 h-px max-w-[32px]" style={{ background: bankStage === 'proof' ? GOLD : '#e2ddd6' }} />}
                          </React.Fragment>
                        ))}
                      </div>

                      {bankStage === 'details' && (
                        <div className="fade-up">
                          {bankMethod ? (
                            <div className="border mb-5" style={{ borderColor: '#e2ddd6' }}>
                              <div className="px-5 py-3 border-b f-j text-[12px] font-semibold uppercase tracking-[0.16em]"
                                style={{ borderColor: '#e2ddd6', background: '#fafaf8', color: GOLD }}>
                                Official Banking Details — {bankMethod.bankName || 'Agency Account'}
                              </div>
                              <div className="px-5 py-2">
                                {[
                                  { label: 'Bank',           val: bankMethod.bankName      },
                                  { label: 'Account Name',   val: bankMethod.accountName   },
                                  { label: 'IBAN',           val: bankMethod.iban          },
                                  { label: 'SWIFT / BIC',    val: bankMethod.swiftBic      },
                                  { label: 'Account Number', val: bankMethod.accountNumber },
                                  { label: 'Routing Number', val: bankMethod.routingNumber },
                                ].filter(r => r.val).map(({ label, val }) => (
                                  <div key={label} className="bank-row">
                                    <span className="f-j text-[13px] uppercase tracking-[0.14em] text-slate-400">{label}</span>
                                    <div className="flex items-center gap-2.5">
                                      <span className="f-j text-[15px] font-medium text-slate-800">{val}</span>
                                      <button className="copy-btn" onClick={() => copy(val, label)}>
                                        {copied === label ? <Check size={11} strokeWidth={2.5} style={{ color: GOLD }} /> : <Copy size={11} strokeWidth={1.5} />}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                <div className="bank-row">
                                  <span className="f-j text-[13px] uppercase tracking-[0.14em] text-slate-400">Payment Reference</span>
                                  <div className="flex items-center gap-2.5">
                                    <span className="f-c font-light" style={{ fontSize: '22px', color: GOLD }}>
                                      {bookingResult.bookingReference}
                                    </span>
                                    <button className="copy-btn" onClick={() => copy(bookingResult.bookingReference, 'ref')}>
                                      {copied === 'ref' ? <Check size={11} strokeWidth={2.5} style={{ color: GOLD }} /> : <Copy size={11} strokeWidth={1.5} />}
                                    </button>
                                  </div>
                                </div>
                                {bankMethod.instructions && (
                                  <div className="py-3 f-j text-[15px] text-slate-500 leading-relaxed">
                                    {bankMethod.instructions}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : methodsError ? (
                            <div className="p-4 border mb-5 text-center" style={{ borderColor: '#e2ddd6', background: '#fafaf8' }}>
                              <p className="f-j text-[15px] text-slate-500 mb-3">
                                We couldn't load the bank details. Your booking is confirmed — this is just the payment screen.
                              </p>
                              <button
                                className="f-j text-[13px] uppercase tracking-[0.14em]"
                                style={{ color: GOLD, background: 'none', border: 'none', cursor: 'pointer' }}
                                onClick={() => bookingResult?.id && fetchBankMethods({ variables: { bookingId: bookingResult.id } })}
                              >
                                Try again
                              </button>
                            </div>
                          ) : !methodsLoading && methodsData ? (
                            <div className="p-4 border mb-5 text-center" style={{ borderColor: '#e2ddd6', background: '#fafaf8' }}>
                              <p className="f-j text-[15px] text-slate-400">
                                No bank transfer account is set up for this agency yet. Please choose Credit Card, or contact us to pay by transfer.
                              </p>
                            </div>
                          ) : (
                            <div className="p-4 border mb-5 text-center" style={{ borderColor: '#e2ddd6', background: '#fafaf8' }}>
                              <p className="f-j text-[15px] text-slate-400">Loading bank details…</p>
                            </div>
                          )}

                          <div className="p-3.5 border flex gap-2.5 mb-5" style={{ borderColor: 'rgba(184,151,90,0.25)', background: GOLD_DIM }}>
                            <Info size={12} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                            <p className="f-j text-[13px] text-slate-500 leading-relaxed">
                              Include <strong>{bookingResult.bookingReference}</strong> as your reference.
                              Your spot is held for <strong>48 hours</strong>. Return here once your bank confirms the transfer.
                            </p>
                          </div>

                          <button className="primary-cta" onClick={() => { setBankStage('proof'); setProof(p => ({ ...p, amount: finalTotal.toFixed(2) })); }}>
                            <span>I've Made the Transfer</span>
                            <ArrowRight size={13} />
                          </button>
                          <button className="ghost-cta mt-2" onClick={() => { setStep(1); setBookingResult(null); }}>
                            <ChevronLeft size={12} /> Back
                          </button>
                        </div>
                      )}

                      {bankStage === 'proof' && (
                        <div className="fade-up space-y-4">
                          <div>
                            <label className="field-label">Amount Transferred <span style={{ color: GOLD }}>*</span></label>
                            <div className="flex border" style={{ borderColor: '#ddd9d2' }}>
                              <div className="px-3 flex items-center f-j text-[15px] font-semibold text-slate-400 border-r" style={{ borderColor: '#ddd9d2', background: '#f8f6f3' }}>USD</div>
                              <input type="number" min="0.01" step="0.01" placeholder="0.00"
                                className="proof-input" style={{ border: 'none' }}
                                value={proof.amount} onChange={e => setProof(p => ({ ...p, amount: e.target.value }))} />
                            </div>
                            <p className="f-j text-[13px] text-slate-400 mt-1">Exact amount shown on your bank receipt</p>
                          </div>
                          <div>
                            <label className="field-label">Bank Transfer Reference <span style={{ color: GOLD }}>*</span></label>
                            <input type="text" className="proof-input" placeholder="e.g. FT2405XXXXXX"
                              value={proof.reference} onChange={e => setProof(p => ({ ...p, reference: e.target.value }))} />
                          </div>
                          <div>
                            <label className="field-label">Bank Transaction ID <span style={{ color: GOLD }}>*</span></label>
                            <input type="text" className="proof-input" placeholder="e.g. TXN-987654321"
                              value={proof.txnId} onChange={e => setProof(p => ({ ...p, txnId: e.target.value }))} />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="field-label">Transfer Date <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
                              <input type="date" className="proof-input" max={todayISO()}
                                value={proof.date} onChange={e => setProof(p => ({ ...p, date: e.target.value }))} />
                            </div>
                            <div>
                              <label className="field-label">Notes <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
                              <input type="text" className="proof-input" placeholder="Anything else…"
                                value={proof.notes} onChange={e => setProof(p => ({ ...p, notes: e.target.value }))} />
                            </div>
                          </div>

                          {error && <div className="p-3 border-l-2 border-red-400 bg-red-50 f-j text-[15px] text-red-600">{error}</div>}

                          <button className="primary-cta" onClick={handleSubmitProof}
                            disabled={submitting || !proof.amount || !proof.reference.trim() || !proof.txnId.trim()}>
                            <span>{submitting ? 'Submitting…' : 'Submit Confirmation'}</span>
                          </button>
                          <button className="ghost-cta" onClick={() => setBankStage('details')}>
                            <ChevronLeft size={12} /> Back to Bank Details
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* ─ Card ─ */}
                  {payMethod === 'card' && (
                    <div className="fade-up space-y-4">
                      <div className="p-4 border flex gap-2.5" style={{ borderColor: 'rgba(184,151,90,0.25)', background: GOLD_DIM }}>
                        <Info size={12} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                        <p className="f-j text-[13px] text-slate-500 leading-relaxed">
                          You'll be redirected to our secure payment gateway to complete your card payment.
                        </p>
                      </div>
                      {error && <div className="p-3 border-l-2 border-red-400 bg-red-50 f-j text-[15px] text-red-600">{error}</div>}
                      <button className="primary-cta" onClick={handleCardPay} disabled={submitting}>
                        <span>{submitting ? 'Redirecting…' : `Pay ${fmt(finalTotal)}`}</span>
                        {!submitting && <ArrowRight size={13} />}
                      </button>
                      <button className="ghost-cta" onClick={() => { setStep(1); setBookingResult(null); }}>
                        <ChevronLeft size={12} /> Back
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ══════════════════════════════
                STEP 3 — CONFIRMED
            ══════════════════════════════ */}
            {step === 3 && bookingResult && (
              <div className="bg-white border fade-up text-center" style={{ borderColor: '#e2ddd6' }}>
                <div className="p-10 sm:p-14">
                  <div className="pop-in w-16 h-16 flex items-center justify-center mx-auto mb-7"
                    style={{ border: `1.5px solid ${GOLD}`, background: GOLD_DIM }}>
                    <CheckCircle2 size={28} strokeWidth={1.5} style={{ color: GOLD }} />
                  </div>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-6 h-px" style={{ background: GOLD }} />
                    <span className="f-j text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                      {payMethod === 'card' ? 'Booking Confirmed' : payMethod === 'later' ? 'Booking Reserved — Payment Pending' : 'Transfer Received — Verifying'}
                    </span>
                    <div className="w-6 h-px" style={{ background: GOLD }} />
                  </div>
                  <h2 className="f-c text-slate-900 font-light mb-3" style={{ fontSize: '36px', lineHeight: 1 }}>
                    {payMethod === 'card' ? 'Your safari is booked.' : "We've reserved your spot."}
                  </h2>
                  <p className="f-j text-[15px] text-slate-500 font-light max-w-sm mx-auto mb-6 leading-relaxed">
                    {payMethod === 'card'
                      ? `Confirmation sent to ${bookingResult.guestEmail}.`
                      : payMethod === 'later'
                      ? `Log in to your MySafari portal anytime to complete payment by bank transfer or card. Reference: ${bookingResult.bookingReference}.`
                      : `Our team will verify your transfer within 1 business day. Reference: ${bookingResult.bookingReference}.`}
                  </p>
                  <div className="inline-flex items-center gap-3 px-5 py-3 mb-7 border" style={{ borderColor: 'rgba(184,151,90,0.3)', background: '#fafaf8' }}>
                    <span className="f-j text-[12px] uppercase tracking-[0.16em] text-slate-400">Booking Ref</span>
                    <span className="f-c font-light text-slate-800" style={{ fontSize: '25px' }}>{bookingResult.bookingReference}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-px mb-8 text-left" style={{ background: '#e8e4de' }}>
                    {[
                      { label: 'Departure', value: formatDate(bookingResult.startDate) },
                      { label: 'Return',    value: formatDate(bookingResult.endDate)   },
                      { label: 'Guests',    value: `${bookingResult.numberOfTourists}` },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-white p-4">
                        <p className="f-j text-[12px] uppercase tracking-[0.16em] text-slate-400 mb-1">{label}</p>
                        <p className="f-c font-light text-slate-800" style={{ fontSize: '18px' }}>{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-5 border mb-6" style={{ borderColor: 'rgba(184,151,90,0.3)', background: GOLD_DIM }}>
                    <p className="f-j text-[13px] uppercase tracking-[0.16em] mb-2" style={{ color: GOLD }}>
                      {payMethod === 'later' ? 'Complete Payment & Your Profile' : 'Complete Your Profile'}
                    </p>
                    <p className="f-j text-[15px] text-slate-600 font-light mb-4 leading-relaxed">
                      {payMethod === 'later'
                        ? 'Pay by bank transfer or card, and add passport details for each traveller, in your MySafari portal — needed 30 days before departure.'
                        : 'Add passport details for each traveller in your MySafari portal — needed 30 days before departure.'}
                    </p>
                    <a href={bookingResult.portalUrl} className="primary-cta" style={{ display: 'inline-flex', width: 'auto', padding: '12px 24px' }}>
                      <span>Open My Safari Portal</span>
                      <ArrowRight size={12} />
                    </a>
                  </div>

                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/tours" className="ghost-cta" style={{ width: 'auto' }}>
                      Explore More <ArrowRight size={12} strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ══════════════════════════════
              SIDEBAR
          ══════════════════════════════ */}
          {step < 3 && (
            <div className="lg:col-span-4 space-y-4">

              {hasBookableDates && (
                <div className="summary-card overflow-hidden">
                  <div className="relative h-44 overflow-hidden">
                    <img src={tour.featuredImage} alt={tour.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(8,14,26,0.7), transparent 60%)' }} />
                    <div className="absolute bottom-4 left-4">
                      {location && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <MapPin size={10} strokeWidth={1.5} style={{ color: GOLD }} />
                          <span className="f-j text-[13px] text-white/80">{location}</span>
                        </div>
                      )}
                      <span className="f-j text-[12px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>{tour.category?.name}</span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="f-c text-slate-900 font-light leading-snug mb-3" style={{ fontSize: '25px' }}>{tour.name}</h3>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={i} size={9} fill={i <= Math.round(tour.averageRating) ? GOLD : 'none'} stroke={GOLD} strokeWidth={1.5} />
                        ))}
                      </div>
                      <span className="f-j text-[13px] text-slate-400">{tour.averageRating} · {tour.totalReviews} reviews</span>
                    </div>

                    <div className="flex gap-4 mb-4">
                      <div className="flex items-center gap-1.5">
                        <Clock size={11} strokeWidth={1.5} style={{ color: GOLD }} />
                        <span className="f-j text-[13px] text-slate-500">{tour.durationLabel}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.5} style={{ color: GOLD }} />
                        <span className="f-j text-[13px] text-slate-500">Max {tour.maxParticipants}</span>
                      </div>
                    </div>

                    {selectedDep && (
                      <div className="p-3 mb-4" style={{ background: GOLD_DIM, border: `1px solid rgba(184,151,90,0.3)` }}>
                        <p className="f-j text-[12px] uppercase tracking-[0.16em] mb-1" style={{ color: GOLD }}>Selected Departure</p>
                        <p className="f-c text-slate-800 font-light" style={{ fontSize: '18px' }}>
                          {formatDate(selectedDep.departureDate)}
                        </p>
                        <p className="f-j text-[13px] text-slate-400">Returns {formatDate(selectedDep.returnDate)}</p>
                      </div>
                    )}

                    <div className="gold-rule mb-4" />

                    <PricingBreakdown
                      lines={lines}
                      totals={{
                        subtotal,
                        tax,
                        taxLabel: pricing?.taxLabel,
                        taxNote: 'On agency service fee · park fees carry their own tax',
                        total: finalTotal,
                        perPerson,
                        currency: 'USD',
                      }}
                      theme="light"
                      showPerPerson={true}
                    />
                  </div>
                </div>
              )}

              {hasBookableDates && (
                <div className="summary-card p-5">
                  {[
                    { icon: Shield, label: tour.cancellationPolicy?.freeDateChanges ? 'Free date changes' : 'Cancellation policy', sub: tour.cancellationPolicy?.name || 'See full policy' },
                    { icon: Zap,    label: 'Instant confirmation', sub: 'Email within 5 minutes'    },
                    { icon: Lock,   label: 'Secure payment',       sub: '256-bit SSL encryption'    },
                    { icon: Clock,  label: 'Complete later',       sub: 'Passport details via portal' },
                  ].map(({ icon: Icon, label, sub }) => (
                    <div key={label} className="trust-item">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ border: `1px solid ${GOLD_DIM}` }}>
                        <Icon size={12} strokeWidth={1.5} style={{ color: GOLD }} />
                      </div>
                      <div>
                        <p className="f-j text-[13px] font-medium text-slate-700 uppercase tracking-widest">{label}</p>
                        <p className="f-j text-[13px] text-slate-400 font-light">{sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tour.agency && (
                <div className="p-5 border text-center" style={{ borderColor: '#e2ddd6', background: '#FAFAF8' }}>
                  <p className="f-j text-[13px] uppercase tracking-[0.16em] text-slate-400 mb-2">Need assistance?</p>
                  <p className="f-c text-slate-700 font-light mb-3" style={{ fontSize: '21px' }}>
                    {tour.agency.name}
                  </p>
                  {tour.agency.phone && (
                    <a href={`tel:${tour.agency.phone}`}
                      className="f-j text-[13px] uppercase tracking-[0.14em] block mb-1 transition-colors hover:opacity-70"
                      style={{ color: GOLD }}>
                      {tour.agency.phone}
                    </a>
                  )}
                  {tour.agency.primaryEmail && (
                    <a href={`mailto:${tour.agency.primaryEmail}`}
                      className="f-j text-[13px] text-slate-400 font-light block hover:text-slate-600 transition-colors">
                      {tour.agency.primaryEmail}
                    </a>
                  )}
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  );
}